import { actionCreator, ActionReturnType, Loading, loadingNotStarted, loadingIsLoading, loadingFinished, loadingFailed } from "@itk/typescript-utils";
import {
  Model_AttributeDetailsGet,
  Model_ConfigurationGet,
  Model_ConfiguratorOrderOut,
  Model_ProjectLineItemAttributePut,
  Model_ProjectLineItemExtendedGet,
  Model_ProjectLineItemPut
} from "src/backend/openapi/v1";
import { MakeSomeRequired } from "src/utils/typescript-utils";

export enum Action {
  GetConfigurationRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETCONFIGURATIONREQUEST",
  GetConfigurationSuccess = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETCONFIGURATIONSUCCESS",
  GetConfigurationFail = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETCONFIGURATIONFAIL",

  UpdateProjectLineItemAttributeRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEMATTRIBUTEREQUEST",
  UpdateProjectLineItemRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEMREQUEST",

  SetSelectedProjectLineItemId = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:SETSELECTEDPROJECTLINEITEMID",

  UpdateProjectLineItemAttributes = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEMATTRIBUTES",
  UpdateProjectLineItem = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:UPDATEPROJECTLINEITEM",

  GetGenerateConfigurationOrderRequest = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETGENERATECONFIGURATIONORDERREQUEST",
  GetGenerateConfigurationOrderSuccess = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:GETGENERATECONFIGURATIONORDERSUCCESS",

  Clear = "@@ITK:SRC:PAGES:CONFIGURE-PRODUCT:CONFIGURE-PRODUCT-STORE.TS:ACTION:CLEAR"
}

type TConfigureProductState = {
  configurationState: Loading<Model_ConfigurationGet>;
  selectedProjectLineItemId?: string;
  generateConfigurationOrder?: Model_ConfiguratorOrderOut;
};

const initialState: TConfigureProductState = {
  configurationState: loadingNotStarted,
  selectedProjectLineItemId: undefined,
  generateConfigurationOrder: undefined
};

export const configureProductActions = {
  getConfigurationRequest: (configurationId: string) => actionCreator(Action.GetConfigurationRequest, { configurationId }),
  getConfigurationSuccess: (data: Model_ConfigurationGet) => actionCreator(Action.GetConfigurationSuccess, { data }),
  getConfigurationFail: () => actionCreator(Action.GetConfigurationFail),

  updateProjectLineItemRequest: (data: MakeSomeRequired<Model_ProjectLineItemPut, "projectLineItemId" | "projectId">) => actionCreator(Action.UpdateProjectLineItemRequest, { data }),

  updateProjectLineItemAttributeRequest: (data: Required<Model_ProjectLineItemAttributePut>) => actionCreator(Action.UpdateProjectLineItemAttributeRequest, { data }),

  updateProjectLineItemAttributes: (projectLineItemId: string, attributes: Model_AttributeDetailsGet[]) => actionCreator(Action.UpdateProjectLineItemAttributes, { projectLineItemId, attributes }),
  updateProjectLineItem: (data: Model_ProjectLineItemExtendedGet) => actionCreator(Action.UpdateProjectLineItem, { data }),

  setSelectedProjectLineItemId: (projectLineItemId: TConfigureProductState["selectedProjectLineItemId"]) => actionCreator(Action.SetSelectedProjectLineItemId, { projectLineItemId }),

  getGenerateConfigurationOrderRequest: (projectId: string, configurationId: string) => actionCreator(Action.GetGenerateConfigurationOrderRequest, { projectId, configurationId }),
  getGenerateConfigurationOrderSuccess: (data: Model_ConfiguratorOrderOut) => actionCreator(Action.GetGenerateConfigurationOrderSuccess, { data }),

  clear: () => actionCreator(Action.Clear)
};

export const configureProductReducer = (state = initialState, action: ActionReturnType<typeof configureProductActions>): TConfigureProductState => {
  switch (action.type) {
    case Action.GetConfigurationRequest:
      return { ...state, configurationState: loadingIsLoading };
    case Action.GetConfigurationSuccess:
      return { ...state, configurationState: loadingFinished(action.payload.data) };
    case Action.GetConfigurationFail:
      return { ...state, configurationState: loadingFailed };

    case Action.SetSelectedProjectLineItemId:
      return { ...state, selectedProjectLineItemId: action.payload.projectLineItemId };

    case Action.UpdateProjectLineItemAttributes: {
      if (state.configurationState.loading !== "has-loaded") return { ...state };
      return {
        ...state,
        configurationState: loadingFinished({
          ...state.configurationState.data,
          projectLineItems: state.configurationState.data.projectLineItems.map(item =>
            item.projectLineItemId === action.payload.projectLineItemId ? { ...item, attributes: action.payload.attributes } : item
          )
        })
      };
    }

    case Action.UpdateProjectLineItem: {
      const newConfigurationState = state.configurationState.loading === "has-loaded" ? { ...state.configurationState.data } : undefined;
      if (newConfigurationState) {
        newConfigurationState.configurationName = action.payload.data.description;
        return { ...state, configurationState: loadingFinished({ ...newConfigurationState }) };
      }
      return { ...state };
    }

    case Action.GetGenerateConfigurationOrderSuccess:
      return { ...state, generateConfigurationOrder: action.payload.data };

    case Action.Clear:
      return { ...initialState };

    default:
      return state;
  }
};
